import React from "react"
import Footer from "../Components/Outlets/Footer"
import Nav from "../Components/Outlets/Nav"
import AdmissionForm from "./AdmissionForm"
import { Link } from "react-router-dom"
import { motion } from "framer-motion"

const steps = [
	{
		number: "01",
		icon: "ri-file-text-line",
		title: "Apply Online",
		text: "Fill in the admission form with student details, academic history, and preferred program stream.",
	},
	{
		number: "02",
		icon: "ri-team-line",
		title: "Counseling Session",
		text: "Meet our academic team to choose the right stream and get a clear picture of the career pathway.",
	},
	{
		number: "03",
		icon: "ri-shield-check-line",
		title: "Confirm Enrollment",
		text: "Complete joining formalities, pay the fees, and reserve the seat for the selected program.",
	},
]

const highlights = [
	{
		icon: "ri-home-heart-line",
		title: "Fully Residential",
		text: "24/7 supervised campus life with safe accommodation, structured study hours, and peer learning.",
	},
	{
		icon: "ri-award-line",
		title: "Integrated Coaching",
		text: "NEET, JEE, CA & CMA coaching is built into the academic calendar — no separate tuition needed.",
	},
	{
		icon: "ri-shield-star-line",
		title: "Boys-Only Campus",
		text: "Exclusively for young men — a focused, safe, and empowering environment for serious learners.",
	},
]

const documents = [
	{ icon: "ri-image-line", label: "Recent passport-size photo" },
	{ icon: "ri-fingerprint-line", label: "Birth certificate or Aadhaar card" },
	{ icon: "ri-book-2-line", label: "Previous school records & TC" },
	{ icon: "ri-phone-line", label: "Parent or guardian contact details" },
]

const faqs = [
	{
		q: "Who can apply to Branmont?",
		a: "Students completing Class 10 and seeking +1 Science or Commerce can apply. The school is exclusively for boys.",
	},
	{
		q: "Is the campus fully residential?",
		a: "Yes. Branmont is a fully residential school with structured living, supervised study, and 24/7 care.",
	},
	{
		q: "When does admission open?",
		a: "Admissions for 2025–26 are currently open. Early applications are encouraged as seats are limited.",
	},
	{
		q: "What programs are offered?",
		a: "Science (+1/+2 with NEET/JEE coaching) and Commerce (+1/+2 with CA/CMA Foundation) streams are available.",
	},
]

const Admission = () => {
	return (
		<div className="admission-page-shell">
			<Nav />

			<section className="inner-hero admission-hero">
				<div className="container-main">
					<motion.div
						className="inner-hero-content"
						initial={{ opacity: 0, y: 35 }}
						animate={{ opacity: 1, y: 0 }}
						transition={{ duration: 0.75 }}
					>
						<span className="inner-eyebrow">Admissions 2025–26</span>
						<h1 className="inner-hero-title montserrat">Begin a focused path to global careers</h1>
						<p className="inner-hero-copy">
							Apply for Branmont Global Secondary School and take the next step toward residential academics,
							intensive exam preparation, and guided career growth.
						</p>
						<div className="inner-hero-actions">
							<a href="#application-form" className="hero-primary-btn">
								Fill Application <i className="ri-arrow-down-line"></i>
							</a>
							<Link to="/contact" className="hero-secondary-btn">
								Talk to Us
							</Link>
						</div>
					</motion.div>
				</div>
			</section>

			<main className="admission-redesign">

				<section className="admission-highlights-section">
					<div className="container-main">
						<div className="section-heading-center">
							<span className="section-eyebrow">Why Branmont</span>
							<h2 className="section-title montserrat">An education designed for serious learners</h2>
							<p className="section-copy">
								More than a school — a structured path from Class 11 to competitive exams and global careers.
							</p>
						</div>
						<div className="row g-4">
							{highlights.map((item, index) => (
								<div className="col-lg-4 col-md-6" key={item.title}>
									<motion.div
										className="admission-highlight-card"
										initial={{ opacity: 0, y: 24 }}
										whileInView={{ opacity: 1, y: 0 }}
										viewport={{ once: true }}
										transition={{ duration: 0.55, delay: index * 0.08 }}
									>
										<div className="admission-highlight-icon">
											<i className={item.icon}></i>
										</div>
										<h3>{item.title}</h3>
										<p>{item.text}</p>
									</motion.div>
								</div>
							))}
						</div>
					</div>
				</section>

				<section className="admission-overview-section">
					<div className="container-main">
						<div className="section-heading-row">
							<div>
								<span className="section-eyebrow">Admission Journey</span>
								<h2 className="section-title montserrat">Clear, guided, and parent-friendly</h2>
							</div>
							<p className="section-copy">
								The admission team helps families understand program options, eligibility,
								and the right preparation track for each student.
							</p>
						</div>

						<div className="row g-4">
							{steps.map((step, index) => (
								<div className="col-lg-4 col-md-6" key={step.number}>
									<motion.div
										className="admission-step-panel"
										initial={{ opacity: 0, y: 24 }}
										whileInView={{ opacity: 1, y: 0 }}
										viewport={{ once: true }}
										transition={{ duration: 0.55, delay: index * 0.08 }}
									>
										<div className="admission-step-top">
											<span>{step.number}</span>
											<div className="admission-step-icon-wrap">
												<i className={step.icon}></i>
											</div>
										</div>
										<h3>{step.title}</h3>
										<p>{step.text}</p>
									</motion.div>
								</div>
							))}
						</div>
					</div>
				</section>

				<section className="admission-form-section" id="application-form">
					<div className="container-main">
						<div className="admission-form-layout">
							<aside className="admission-support-panel">
								<span className="section-eyebrow">Before You Apply</span>
								<h2 className="section-title montserrat">Keep these details ready</h2>
								<p className="section-copy">
									The form may ask for student information, previous school details, and parent contact
									information. Keep the basics nearby so the application stays smooth.
								</p>
								<div className="document-list">
									{documents.map((item) => (
										<div className="document-item" key={item.label}>
											<i className={item.icon}></i>
											<span>{item.label}</span>
										</div>
									))}
								</div>
								<div className="admission-help-box">
									<i className="ri-customer-service-2-line"></i>
									<div>
										<strong>Need guidance?</strong>
										<p>Our team can help you choose the right stream and preparation pathway.</p>
									</div>
								</div>
							</aside>

							<div className="admission-form-column">
								<AdmissionForm />
							</div>
						</div>
					</div>
				</section>

				<section className="admission-faq-section">
					<div className="container-main">
						<div className="section-heading-center">
							<span className="section-eyebrow">FAQ</span>
							<h2 className="section-title montserrat">Common questions answered</h2>
						</div>
						<div className="admission-faq-grid">
							{faqs.map((faq, index) => (
								<motion.div
									className="admission-faq-card"
									key={faq.q}
									initial={{ opacity: 0, y: 20 }}
									whileInView={{ opacity: 1, y: 0 }}
									viewport={{ once: true }}
									transition={{ duration: 0.5, delay: index * 0.07 }}
								>
									<div className="faq-question">
										<i className="ri-question-line"></i>
										<h4>{faq.q}</h4>
									</div>
									<p>{faq.a}</p>
								</motion.div>
							))}
						</div>
					</div>
				</section>

				<section className="admission-cta-section">
					<div className="container-main">
						<motion.div
							className="admission-cta-box"
							initial={{ opacity: 0, y: 24 }}
							whileInView={{ opacity: 1, y: 0 }}
							viewport={{ once: true }}
							transition={{ duration: 0.6 }}
						>
							<div className="admission-cta-icon-wrap">
								<i className="ri-phone-line"></i>
							</div>
							<div className="admission-cta-text">
								<h3 className="montserrat">Still have questions?</h3>
								<p>Our admission counselors are available Monday–Saturday, 8 AM to 5 PM.</p>
							</div>
							<Link to="/contact" className="hero-primary-btn">
								Contact Us <i className="ri-arrow-right-line"></i>
							</Link>
						</motion.div>
					</div>
				</section>
			</main>

			<Footer />
		</div>
	)
}

export default Admission
